import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { API_URLS, API_CONFIG } from '../../config/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  ArrowLeft,
  Star,
  Clock,
  Users,
  CheckCircle,
  Zap,
  Globe,
  Palette,
  Smartphone,
  Server,
  Megaphone,
  TrendingUp,
  Award,
  Shield,
  MessageSquare,
  Calendar,
  Eye,
  Download,
  ExternalLink,
  Play,
  Code,
  Database,
  Layers,
  Target,
  Rocket,
  Heart,
  ThumbsUp,
  Share2,
  Bookmark,
  Briefcase,
  FileText,
  Settings,
  Lightbulb,
  BarChart3,
  Mail,
  Phone,
  MapPin,
  Timer,
  CheckSquare,
  ArrowRight,
  Plus,
  Minus,
  Info,
  AlertCircle,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  X,
  Tag,
  Image,
  Video,
  Monitor,
  Smartphone as Mobile,
  Tablet
} from 'lucide-react';
import ProjectQuestionForm from '../ProjectQuestionForm';
import ConsultationForm from '../ConsultationForm';
import MediaDisplay from '../MediaDisplay';

// Hover scroll effect styles
const customStyles = `
  .group:hover .group-hover\\:object-bottom {
    transition: all 35s ease;
  }
  .image-short { transition: all 5s ease !important; }
  .image-medium { transition: all 12s ease !important; }
  .image-long { transition: all 25s ease !important; }
  .image-very-long { transition: all 40s ease !important; }
`;

function getImageTransitionClass(naturalHeight) {
  if (!naturalHeight) return '';
  if (naturalHeight < 800) return 'image-short';
  if (naturalHeight < 1500) return 'image-medium';
  if (naturalHeight < 2500) return 'image-long';
  return 'image-very-long';
}

const ProjectDetailPage = () => {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [showConsultationForm, setShowConsultationForm] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [contentReady, setContentReady] = useState(false);

  // Fade-in animasyonu için
  useEffect(() => {
    const timer = setTimeout(() => {
      setContentReady(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  // Icon mapping
  const iconMap = {
    Briefcase, Globe, Palette, Smartphone, Zap, Server, 
    Megaphone, Code, Database, Layers, Target, Rocket,
    Heart, Star, Award, Shield, Users, TrendingUp
  };

  useEffect(() => {
    const fetchProjectData = async () => {
      try {
        setLoading(true);
        
        if (!projectId) {
          setError('Proje ID\'si bulunamadı');
          setLoading(false);
          return;
        }
        
        const url = `${API_URLS.PROJECT_DETAILS}&id=${encodeURIComponent(projectId)}`;
        const response = await fetch(url);
        if (!response.ok) {
          if (response.status === 404) {
            setError('Proje bulunamadı veya artık mevcut değil');
          } else {
            setError('Proje verileri yüklenirken hata oluştu');
          }
          setLoading(false);
          return;
        }
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          const text = await response.text();
          throw new Error(`Non-JSON response: ${text.substring(0, 120)}...`);
        }
        const proj = await response.json();
        setProject({
          id: proj.id,
          name: proj.name,
          description: proj.description || '',
          detailed_description: proj.detailed_description || '',
          image_url: proj.image_url || 'https://placehold.co/800x600/e0e7ff/6366f1?text=Proje+Görseli',
          price: Number(proj.price || 0),
          original_price: proj.original_price ? Number(proj.original_price) : null,
          discount: proj.discount ? Number(proj.discount) : null,
          duration: proj.duration || '',
          rating: proj.rating ? Number(proj.rating) : 0,
          reviews_count: proj.reviews_count ? Number(proj.reviews_count) : 0,
          features: Array.isArray(proj.features) ? proj.features : [],
          client_name: proj.client_name || '',
          team_size: proj.team_size ? Number(proj.team_size) : null,
          launch_date: proj.launch_date || null,
          project_year: proj.project_year || '',
          status: proj.status || 'completed',
          category_id: proj.category_id,
          category_name: proj.category_name || 'Proje',
          technologies: Array.isArray(proj.technologies) ? proj.technologies : [],
          gallery: Array.isArray(proj.gallery) ? proj.gallery : [],
          testimonials: Array.isArray(proj.testimonials) ? proj.testimonials : [],
          process_steps: Array.isArray(proj.process_steps) ? proj.process_steps : [],
          statistics: (proj.statistics && typeof proj.statistics === 'object') ? proj.statistics : {},
          project_url: proj.project_url || null,
          demo_url: proj.demo_url || null
        });
        // Kategori slug'ını çek
        let categorySlug = null;
        if (proj.category_id) {
          try {
            const categoryResponse = await fetch(API_URLS.PROJECT_CATEGORIES);
            if (categoryResponse.ok) {
              const categoryData = await categoryResponse.json();
              if (Array.isArray(categoryData)) {
                const foundCategory = categoryData.find(cat => cat.id === proj.category_id);
                if (foundCategory && foundCategory.slug) {
                  categorySlug = foundCategory.slug;
                }
              }
            }
          } catch (err) {
            console.error('Error fetching category slug:', err);
          }
        }
        setCategory({ 
          id: proj.category_id, 
          slug: categorySlug,
          name: proj.category_name || 'Proje', 
          icon: 'Briefcase', 
          color: 'from-blue-500 to-cyan-500', 
          bg_color: 'bg-blue-50' 
        });
      } catch (error) {
        if (!error.message || error.message.includes('HTTP 404')) {
          setError('Proje bulunamadı veya artık mevcut değil');
        } else {
          setError('Proje verileri yüklenirken hata oluştu');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProjectData();
  }, [projectId]);

  const handlePrevImage = () => {
    if (project?.gallery && project.gallery.length > 0) {
      setLightboxIndex((prev) => (prev === 0 ? project.gallery.length - 1 : prev - 1));
    }
  };

  const handleNextImage = () => {
    if (project?.gallery && project.gallery.length > 0) {
      setLightboxIndex((prev) => (prev === project.gallery.length - 1 ? 0 : prev + 1));
    }
  };

  useEffect(() => {
    if (!lightboxOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setLightboxOpen(false);
      }
      if (e.key === 'ArrowLeft') {
        if (project?.gallery && project.gallery.length > 0) {
          setLightboxIndex((prev) => (prev === 0 ? project.gallery.length - 1 : prev - 1));
        }
      }
      if (e.key === 'ArrowRight') {
        if (project?.gallery && project.gallery.length > 0) {
          setLightboxIndex((prev) => (prev === project.gallery.length - 1 ? 0 : prev + 1));
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, project]);

  // Helper functions
  const IconComponent = iconMap[category?.icon] || Briefcase;

  const getProcessIcon = (iconName) => {
    const processIconMap = {
      Target, Palette, Code, CheckCircle, Rocket, Users, Clock, Settings
    };
    return processIconMap[iconName] || Target;
  };

  const getTechIcon = (iconName) => {
    const techIconMap = {
      Code, Server, Database, Shield, Globe
    };
    return techIconMap[iconName] || Code;
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      'completed': 'Tamamlandı',
      'in_progress': 'Devam Ediyor',
      'pending': 'Beklemede',
      'active': 'Aktif',
      'on_hold': 'Askıda',
      'cancelled': 'İptal Edildi'
    };
    return statusMap[status] || 'Tamamlandı';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Proje yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            {error || 'Proje Bulunamadı'}
          </h1>
          <Link 
            to="/projeler" 
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Projeler sayfasına dön
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{customStyles}</style>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Hero Section */}
      <section 
        className="bg-gradient-to-r from-blue-600 via-purple-600 to-blue-800 text-white py-20 relative overflow-hidden"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-6">
            <Link 
              to="/projeler" 
              className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Projeler
            </Link>
            <ChevronRight className="w-4 h-4 text-white/60" />
            <Link 
              to={`/kategori/${category?.slug || project.category_id}`} 
              className="text-white/80 hover:text-white transition-colors"
            >
              {project.category_name}
            </Link>
            <ChevronRight className="w-4 h-4 text-white/60" />
            <span className="text-white font-medium">{project.name}</span>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-6">
                <div className="inline-flex items-center space-x-3 bg-white/20 backdrop-blur-lg px-6 py-3 rounded-full text-lg font-medium">
                  <IconComponent className="w-6 h-6" />
                  <span>{project.category_name}</span>
                  <Badge className="bg-blue-400 text-blue-900">Proje</Badge>
                </div>
                
                <h1 className="text-4xl md:text-6xl font-bold leading-tight">
                  {project.name}
                </h1>
                
                <p className="text-xl text-blue-100 leading-relaxed">
                  {project.description}
                </p>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-6">
                {[
                  { icon: Star, number: project.rating, label: 'Puan' },
                  { icon: Users, number: project.reviews_count, label: 'Değerlendirme' },
                  { icon: Clock, number: project.duration, label: 'Süre' }
                ].map((stat, index) => (
                  <div key={index} className="text-center">
                    <stat.icon className="w-8 h-8 mx-auto mb-2" />
                    <p className="text-2xl font-bold">{stat.number}</p>
                    <p className="text-blue-200 text-sm">{stat.label}</p>
                  </div>
                ))}
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button onClick={() => setShowQuestionForm(true)} className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-3 text-lg">
                  <MessageSquare className="w-5 h-5 mr-2" />
                  Proje Hakkında Konuş
                </Button>
                {project.project_url && (
                  <Button 
                    variant="outline" 
                    className="border-white text-white hover:bg-white hover:text-blue-600 px-8 py-3 text-lg"
                    onClick={() => window.open(project.project_url, '_blank')}
                  >
                    <ExternalLink className="w-5 h-5 mr-2" />
                    Canlı Siteyi Gör
                  </Button>
                )}
              </div>
            </div>
            
            <div className="relative group">
              <div className="aspect-[4/5] bg-white/10 backdrop-blur-lg rounded-2xl overflow-hidden border border-white/20">
                <MediaDisplay
                  src={project.image_url}
                  alt={project.name}
                  className="w-full h-full object-cover object-top transition-all duration-[35s] ease group-hover:object-bottom"
                  videoProps={{ controls: true }}
                  imgProps={{
                    onLoad: (e) => {
                      const nh = e.target.naturalHeight;
                      const cls = getImageTransitionClass(nh);
                      if (cls) e.target.classList.add(cls);
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <div 
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        {/* Tabs */}
        <div className="mb-8">
          <div className="border-b border-gray-200">
            {/* Desktop Tabs */}
            <nav className="hidden md:flex -mb-px space-x-8">
              {[
                { id: 'overview', label: 'Genel Bakış', icon: Eye },
                { id: 'features', label: 'Özellikler', icon: CheckCircle },
                { id: 'process', label: 'Süreç', icon: Target },
                { id: 'gallery', label: 'Galeri', icon: Image },
                { id: 'testimonials', label: 'Yorumlar', icon: MessageSquare }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </nav>
            
            {/* Mobile Tabs - Wrap Layout */}
            <nav className="md:hidden -mb-px">
              <div className="flex flex-wrap gap-2 pb-2">
                {[
                  { id: 'overview', label: 'Genel Bakış', shortLabel: 'Genel', icon: Eye },
                  { id: 'features', label: 'Özellikler', shortLabel: 'Özellikler', icon: CheckCircle },
                  { id: 'process', label: 'Süreç', shortLabel: 'Süreç', icon: Target },
                  { id: 'gallery', label: 'Galeri', shortLabel: 'Galeri', icon: Image },
                  { id: 'testimonials', label: 'Yorumlar', shortLabel: 'Yorumlar', icon: MessageSquare }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-1.5 py-2 px-3 border-b-2 font-medium text-xs ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600 bg-blue-50 rounded-t-lg'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <tab.icon className="w-4 h-4 flex-shrink-0" />
                    <span>{tab.shortLabel}</span>
                  </button>
                ))}
              </div>
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-12">
            {/* Project Details */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-8">
                <Card>
                  <CardHeader>
                    <CardTitle>Proje Hakkında</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 leading-relaxed">
                      {showFullDescription ? project.detailed_description : project.description}
                    </p>
                    {project.detailed_description && (
                      <button
                        onClick={() => setShowFullDescription(!showFullDescription)}
                        className="mt-4 text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                      >
                        {showFullDescription ? (
                          <>
                            <Minus className="w-4 h-4" />
                            Daha Az Göster
                          </>
                        ) : (
                          <>
                            <Plus className="w-4 h-4" />
                            Devamını Oku
                          </>
                        )}
                      </button>
                    )}
                  </CardContent>
                </Card>

                {/* Technologies */}
                <Card>
                  <CardHeader>
                    <CardTitle>Kullanılan Teknolojiler</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {(project.technologies || []).map((tech, index) => {
                        const TechIcon = getTechIcon(tech.icon);
                        return (
                          <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                              <TechIcon className="w-4 h-4 text-blue-600" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{tech.name}</p>
                              <p className="text-sm text-gray-500">{tech.category}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="space-y-6">
                {/* Project Info */}
                <Card>
                  <CardHeader>
                    <CardTitle>Proje Bilgileri</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Müşteri:</span>
                      <span className="font-medium">{project.client_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Kategori:</span>
                      <span className="font-medium">{project.category_name}</span>
                    </div>
                    {project.team_size != null && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Ekip Boyutu:</span>
                        <span className="font-medium">{project.team_size} kişi</span>
                      </div>
                    )}
                    {project.launch_date && !Number.isNaN(Date.parse(project.launch_date)) && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Başlangıç:</span>
                        <span className="font-medium">{new Date(project.launch_date).toLocaleDateString('tr-TR')}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-600">Durum:</span>
                      <Badge className="bg-green-500 text-white">
                        {getStatusLabel(project.status)}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>

                {/* Action Buttons */}
                <div className="space-y-3">
                  <Button className="w-full" onClick={() => setShowQuestionForm(true)}>
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Proje Hakkında Konuş
                  </Button>
                  {project.project_url && (
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => window.open(project.project_url, '_blank')}
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Canlı Siteyi Gör
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'features' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
              <Card>
                <CardHeader>
                  <CardTitle>Proje Özellikleri</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {(project.features || []).map((feature, index) => (
                      <div key={index} className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                        <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        </div>
                        <span className="font-medium text-gray-900">{feature}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Statistics */}
              <Card>
                <CardHeader>
                  <CardTitle>Proje İstatistikleri</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
                    {Object.entries(project.statistics || {}).map(([key, value]) => (
                      <div key={key} className="text-center">
                        <p className="text-2xl font-bold text-blue-600">{value}</p>
                        <p className="text-sm text-gray-600 capitalize">
                          {key.replace('_', ' ')}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Project Info */}
              <Card>
                <CardHeader>
                  <CardTitle>Proje Bilgileri</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Müşteri:</span>
                    <span className="font-medium">{project.client_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Kategori:</span>
                    <span className="font-medium">{project.category_name}</span>
                  </div>
                  {project.team_size != null && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Ekip Boyutu:</span>
                      <span className="font-medium">{project.team_size} kişi</span>
                    </div>
                  )}
                  {project.launch_date && !Number.isNaN(Date.parse(project.launch_date)) && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Başlangıç:</span>
                      <span className="font-medium">{new Date(project.launch_date).toLocaleDateString('tr-TR')}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Durum:</span>
                    <Badge className="bg-green-500 text-white">
                      {getStatusLabel(project.status)}
                    </Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Action Buttons */}
              <div className="space-y-3">
                <Button className="w-full" onClick={() => setShowQuestionForm(true)}>
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Proje Hakkında Konuş
                </Button>
                {project.project_url && (
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => window.open(project.project_url, '_blank')}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Canlı Siteyi Gör
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'process' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle>Proje Süreci</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {(project.process_steps || []).map((step, index) => {
                      const ProcessIcon = getProcessIcon(step.icon);
                      return (
                        <div key={index} className="flex items-start gap-4">
                          <div className={`w-12 h-12 bg-gradient-to-r ${category?.color || 'from-blue-500 to-cyan-500'} rounded-full flex items-center justify-center text-white font-bold flex-shrink-0`}>
                            {step.completed ? (
                              <CheckCircle className="w-6 h-6" />
                            ) : (
                              <span>{step.step}</span>
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-lg font-semibold text-gray-900">{step.title}</h3>
                              <Badge variant="secondary">{step.duration}</Badge>
                            </div>
                            <p className="text-gray-600">{step.description}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Proje Bilgileri</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Müşteri:</span>
                    <span className="font-medium">{project.client_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Kategori:</span>
                    <span className="font-medium">{project.category_name}</span>
                  </div>
                  {project.team_size != null && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Ekip Boyutu:</span>
                      <span className="font-medium">{project.team_size} kişi</span>
                    </div>
                  )}
                  {project.launch_date && !Number.isNaN(Date.parse(project.launch_date)) && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Başlangıç:</span>
                      <span className="font-medium">{new Date(project.launch_date).toLocaleDateString('tr-TR')}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Durum:</span>
                    <Badge className="bg-green-500 text-white">
                      {getStatusLabel(project.status)}
                    </Badge>
                  </div>
                </CardContent>
              </Card>

              <div className="space-y-3">
                <Button className="w-full" onClick={() => setShowQuestionForm(true)}>
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Proje Hakkında Konuş
                </Button>
                {project.project_url && (
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => window.open(project.project_url, '_blank')}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Canlı Siteyi Gör
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'gallery' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle>Proje Galerisi</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {(project.gallery || []).map((image, index) => (
                      <div 
                        key={index} 
                        className="aspect-video bg-gray-100 rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => {
                          setLightboxIndex(index);
                          setLightboxOpen(true);
                        }}
                      >
                        <MediaDisplay
                          src={image}
                          alt={`${project.name} - Görsel ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Proje Bilgileri</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Müşteri:</span>
                    <span className="font-medium">{project.client_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Kategori:</span>
                    <span className="font-medium">{project.category_name}</span>
                  </div>
                  {project.team_size != null && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Ekip Boyutu:</span>
                      <span className="font-medium">{project.team_size} kişi</span>
                    </div>
                  )}
                  {project.launch_date && !Number.isNaN(Date.parse(project.launch_date)) && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Başlangıç:</span>
                      <span className="font-medium">{new Date(project.launch_date).toLocaleDateString('tr-TR')}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Durum:</span>
                    <Badge className="bg-green-500 text-white">
                      {getStatusLabel(project.status)}
                    </Badge>
                  </div>
                </CardContent>
              </Card>

              <div className="space-y-3">
                <Button className="w-full" onClick={() => setShowQuestionForm(true)}>
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Proje Hakkında Konuş
                </Button>
                {project.project_url && (
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => window.open(project.project_url, '_blank')}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Canlı Siteyi Gör
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'testimonials' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle>Müşteri Yorumları</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {(project.testimonials || []).map((testimonial) => (
                      <div key={testimonial.id} className="p-6 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-4 mb-4">
                          <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                            <MediaDisplay
                              src={testimonial.avatar}
                              alt={testimonial.name}
                              className="w-full h-full object-cover"
                              videoProps={{
                                className: "w-full h-full object-cover rounded-full"
                              }}
                            />
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                            <p className="text-sm text-gray-600">{testimonial.position}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 mb-3">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                          ))}
                        </div>
                        <p className="text-gray-700 italic">"{testimonial.content}"</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Proje Bilgileri</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Müşteri:</span>
                    <span className="font-medium">{project.client_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Kategori:</span>
                    <span className="font-medium">{project.category_name}</span>
                  </div>
                  {project.team_size != null && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Ekip Boyutu:</span>
                      <span className="font-medium">{project.team_size} kişi</span>
                    </div>
                  )}
                  {project.launch_date && !Number.isNaN(Date.parse(project.launch_date)) && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Başlangıç:</span>
                      <span className="font-medium">{new Date(project.launch_date).toLocaleDateString('tr-TR')}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Durum:</span>
                    <Badge className="bg-green-500 text-white">
                      {getStatusLabel(project.status)}
                    </Badge>
                  </div>
                </CardContent>
              </Card>

              <div className="space-y-3">
                <Button className="w-full" onClick={() => setShowQuestionForm(true)}>
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Proje Hakkında Konuş
                </Button>
                {project.project_url && (
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => window.open(project.project_url, '_blank')}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Canlı Siteyi Gör
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* CTA Section */}
        <div className="mt-16 bg-gradient-to-r from-purple-600 to-blue-600 rounded-3xl p-8 text-white text-center">
          <h2 className="text-3xl font-bold mb-4">
            Benzer Bir Proje İstiyor musunuz?
          </h2>
          <p className="text-purple-100 mb-8 max-w-2xl mx-auto">
            Uzman ekibimizle birlikte {project.category_name.toLowerCase()} alanında başarılı projeler gerçekleştirin.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={() => setShowConsultationForm(true)} className="bg-white text-purple-600 hover:bg-gray-100">
              <MessageSquare className="w-5 h-5 mr-2" />
              Ücretli Danışmanlık
            </Button>
            <Button onClick={() => { window.location.href = '/iletisim'; }} variant="outline" className="border-white text-white hover:bg-white hover:text-purple-600">
              <Calendar className="w-5 h-5 mr-2" />
              İletişime Geç
            </Button>
          </div>
        </div>
      </div>
      {/* Lightbox Modal */}
      {lightboxOpen && project.gallery && project.gallery.length > 0 && (
        <div 
          className="fixed inset-0 bg-black/95 z-[100] flex items-center justify-center"
          onClick={() => setLightboxOpen(false)}
        >
          {/* Close Button */}
          <button
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 z-[110] p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="w-6 h-6 text-white" />
          </button>

          {/* Image Counter */}
          <div className="absolute top-4 left-4 z-[110] px-4 py-2 bg-white/10 rounded-full text-white">
            {lightboxIndex + 1} / {project.gallery.length}
          </div>

          {/* Previous Button */}
          {project.gallery.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handlePrevImage();
              }}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-[110] p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
            >
              <ChevronLeft className="w-8 h-8 text-white" />
            </button>
          )}

          {/* Next Button */}
          {project.gallery.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleNextImage();
              }}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-[110] p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
            >
              <ChevronRight className="w-8 h-8 text-white" />
            </button>
          )}

          {/* Image/Video */}
          <div 
            className="relative mx-4 flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <MediaDisplay
              src={project.gallery[lightboxIndex]}
              alt={`${project.name} - Görsel ${lightboxIndex + 1}`}
              className="max-w-[80vw] max-h-[80vh] object-contain rounded-lg"
              videoProps={{
                controls: true
              }}
            />
          </div>
        </div>
      )}

      {showQuestionForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur z-50 flex items-center justify-center p-4">
          <div className="relative z-50 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <ProjectQuestionForm project={project} onClose={() => setShowQuestionForm(false)} />
          </div>
        </div>
      )}

      {showConsultationForm && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur z-50 flex items-center justify-center p-4 overflow-y-auto"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowConsultationForm(false);
            }
          }}
        >
          <div className="relative z-50 max-w-4xl w-full my-8 max-h-[90vh] overflow-y-auto">
            <ConsultationForm isModal={true} onClose={() => setShowConsultationForm(false)} />
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default ProjectDetailPage;


